import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Empty, Flex, Pagination, Skeleton, Typography, theme } from '@/shared/antd-imports';
import { NotificationOutlined } from '@ant-design/icons';
import { useGetClientUpdatesQuery } from '@/store/updates-api';
import { formatCalendarDate } from '@/utils/money';
import { DisclosureTag } from './AgentProgressChip';

interface UpdatesListProps {
  projectId?: string | null;
  /** Show the project name on each item (the all-projects page). */
  showProject?: boolean;
}

/**
 * Spec 28 §7.5 (stream abill): published updates, newest first — title, period, published date and the first 200
 * characters. Loading, empty ("No updates yet…") and error (retry) states; one column on phones.
 */
export const UpdatesList: React.FC<UpdatesListProps> = ({ projectId = null, showProject = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isFetching, refetch } = useGetClientUpdatesQuery({ projectId, page });
  const updates = data?.body?.updates ?? [];

  if (isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (isError || (data && !data.done)) {
    return (
      <Alert
        type="error"
        showIcon
        message={t('updates.loadError', { defaultValue: 'The updates could not be loaded.' })}
        action={<Button size="small" onClick={() => void refetch()} loading={isFetching}>{t('updates.retry', { defaultValue: 'Retry' })}</Button>}
      />
    );
  }
  if (!updates.length) {
    return <Empty description={t('updates.empty', { defaultValue: 'No updates yet. Your project team will post progress here.' })} />;
  }
  const period = (from: string, to: string) =>
    from === to ? formatCalendarDate(from, i18n.language) : `${formatCalendarDate(from, i18n.language)} – ${formatCalendarDate(to, i18n.language)}`;

  return (
    <Flex vertical gap={12} data-testid="updates-list">
      {updates.map(update => (
        <Card
          key={update.id}
          size="small"
          hoverable
          role="link"
          tabIndex={0}
          onClick={() => navigate(`/updates/${update.id}`)}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              navigate(`/updates/${update.id}`);
            }
          }}
          aria-label={update.title}
          data-testid="update-card"
        >
          <Flex vertical gap={6}>
            <Flex gap={8} wrap align="center" justify="space-between">
              <Typography.Text strong style={{ fontSize: 15, wordBreak: 'break-word' }}>
                <NotificationOutlined aria-hidden style={{ color: token.colorPrimary, marginInlineEnd: 8 }} />
                {update.title}
              </Typography.Text>
              {update.ai_assisted && <DisclosureTag label={update.disclosure_label} />}
            </Flex>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {showProject ? `${update.project_name} · ` : ''}
              {period(update.period_from, update.period_to)}
              {' · '}
              {t('updates.publishedOn', { defaultValue: 'Published {{date}}', date: formatCalendarDate(update.published_at, i18n.language) ?? '' })}
              {update.evidence_count > 0 ? ` · ${t('updates.attachments', { defaultValue: '{{count}} attachments', count: update.evidence_count })}` : ''}
            </Typography.Text>
            <Typography.Paragraph style={{ margin: 0, fontSize: 13 }} ellipsis={{ rows: 3 }}>
              {update.excerpt}
            </Typography.Paragraph>
          </Flex>
        </Card>
      ))}
      {(data?.body?.total ?? 0) > (data?.body?.page_size ?? 20) && (
        <Pagination
          current={page}
          pageSize={data?.body?.page_size ?? 20}
          total={data?.body?.total ?? 0}
          onChange={setPage}
          showSizeChanger={false}
          size="small"
        />
      )}
    </Flex>
  );
};
